import { useCallback } from 'react';
import { ConfigurationRequestError } from '../adapters/configuration-http';
import { MonitoringReadError } from '../adapters/http';
import type {
  ConfigReceipt,
  ConfigurationPort,
  MonitoringConfiguration,
  MonitoringNetwork,
} from './configuration';
import { useMonitoringRead } from './polling';
export interface ConfigurationSnapshot {
  configuration: MonitoringConfiguration;
  network: MonitoringNetwork;
}
export async function readConfigurationSnapshot(
  port: ConfigurationPort,
  signal: AbortSignal,
): Promise<ConfigurationSnapshot> {
  const [configurationRead, networkRead] = await Promise.allSettled([
    port.configuration(signal),
    port.network(signal),
  ]);
  if (configurationRead.status === 'rejected') throw configurationRead.reason;
  if (networkRead.status === 'rejected') throw networkRead.reason;
  const configuration = configurationRead.value;
  const network = networkRead.value;
  if (
    configuration.configRevision !== network.configRevision ||
    configuration.areas.length !== network.areas.length ||
    configuration.sources.length !== network.sources.length ||
    configuration.areas.some(
      (a) =>
        !network.areas.some(
          (n) =>
            n.areaId === a.areaId &&
            n.configuredSources ===
              configuration.sources.filter((s) => s.areaId === a.areaId).length &&
            n.enabledSources ===
              configuration.sources.filter((s) => s.areaId === a.areaId && s.enabled).length,
        ),
    ) ||
    configuration.sources.some(
      (s) => !network.sources.some((n) => n.sourceId === s.sourceId && n.epoch === s.epoch),
    )
  )
    throw new MonitoringReadError('WIRE');
  return { configuration, network };
}
export function useConfigurationSnapshot(port: ConfigurationPort) {
  const read = useCallback(
    (signal: AbortSignal) => readConfigurationSnapshot(port, signal),
    [port],
  );
  return useMonitoringRead('configuration-network', read);
}
export type MutationResult =
  | { kind: 'SAVED'; receipt: ConfigReceipt }
  | { kind: 'CONFLICT'; latest: MonitoringConfiguration | null }
  | { kind: 'ERROR'; message: string };
/** A conflict makes one fresh read and never retries a write or replaces a draft. */
export async function submitConfigurationMutation(
  port: ConfigurationPort,
  mutate: (signal: AbortSignal) => Promise<ConfigReceipt>,
  signal: AbortSignal,
): Promise<MutationResult> {
  try {
    return { kind: 'SAVED', receipt: await mutate(signal) };
  } catch (error) {
    if (signal.aborted) throw error;
    if (
      error instanceof ConfigurationRequestError &&
      error.status === 409 &&
      error.code === 'CONFIG_REVISION_CONFLICT'
    ) {
      try {
        return { kind: 'CONFLICT', latest: await port.configuration(signal) };
      } catch (readError) {
        if (signal.aborted) throw readError;
        return { kind: 'CONFLICT', latest: null };
      }
    }
    return {
      kind: 'ERROR',
      message:
        error instanceof ConfigurationRequestError
          ? `${error.status} ${error.code}`
          : error instanceof Error
            ? error.message
            : '配置请求失败',
    };
  }
}
